import PropTypes from "prop-types";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Divider,
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { Close } from "@mui/icons-material";

// ----------------------------------------------------------------------

FormWrapper.propTypes = {
  children: PropTypes.node.isRequired,
  onClose: PropTypes.func,
  sx: PropTypes.object,
};

export default function FormWrapper({
  open = false,
  variants,
  onClose,
  children,
  sx,
  onSubmit,
  submitText = "Save",
  onReset,
  loading,
  isEditMode,
  isReplicate,
  maxWidth,
  disabled,
  hiddenReset,
  title,
  dialogProps,
  fullScreen,
  showDelete,
  deleteText,
  ...other
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <Dialog
      maxWidth={maxWidth || "sm"}
      fullWidth
      open
      onClose={(_, reason) => {
        if (reason === "backdropClick") return false;
        onClose();
      }}
      PaperProps={{
        sx: isMobile
          ? {
              // Bottom-sheet on mobile: sits at bottom, height = content, max 92vh
              position: "fixed",
              bottom: 0,
              left: 0,
              right: 0,
              m: 0,
              width: "100%",
              maxWidth: "100% !important",
              maxHeight: "92vh",
              borderRadius: "16px 16px 0 0",
              display: "flex",
              flexDirection: "column",
            }
          : {},
      }}
      sx={
        isMobile
          ? {
              "& .MuiDialog-container": {
                alignItems: "flex-end",
              },
            }
          : {}
      }
      {...other}
    >
      {/* Header */}
      <AppBar
        sx={{
          position: "relative",
          background: (theme) => theme.palette.primary.light,
          color: (theme) => theme.palette.primary.dark,
          borderRadius: isMobile ? "16px 16px 0 0" : 0,
          flexShrink: 0,
        }}
        // variant="outlined"
        elevation={0}
      >
        <Toolbar variant="dense">
          <Typography
            sx={{ ml: 2, flex: 1 }}
            variant={isMobile ? "subtitle1" : "h6"}
            component="div"
            noWrap
          >
            {title || ""}
          </Typography>
          <IconButton
            edge="start"
            color="inherit"
            onClick={onClose}
            aria-label="close"
            size="small"
          >
            <Close />
          </IconButton>
        </Toolbar>
      </AppBar>

      {/* Scrollable content - grows to fit fields, scrolls if too tall */}
      <DialogContent
        dividers
        sx={{
          px: { xs: 1.5, sm: 3 },
          py: { xs: 1.5, sm: 2 },
          overflowY: "auto",
          flexGrow: 1,
        }}
      >
        {children}
      </DialogContent>

      <Divider />

      {/* Actions - always pinned to bottom */}
      <DialogActions
        sx={{
          px: { xs: 1.5, sm: 2 },
          py: { xs: 1.5, sm: 1 },
          flexWrap: "wrap",
          gap: 1,
          flexShrink: 0,
          justifyContent: { xs: "stretch", sm: "flex-end" },
          "& .MuiButton-root, & .MuiLoadingButton-root": {
            flex: { xs: 1, sm: "none" },
            minWidth: { xs: 0, sm: 80 },
          },
        }}
      >
        {onSubmit && (
          <LoadingButton
            onClick={onSubmit}
            variant="contained"
            color="primary"
            loading={loading}
            disabled={disabled}
            size={isMobile ? "small" : "medium"}
          >
            {isReplicate ? "Replicate" : isEditMode ? "Update" : submitText}
          </LoadingButton>
        )}
        {onReset && !hiddenReset && (
          <Button
            onClick={onReset}
            variant="contained"
            color="warning"
            size={isMobile ? "small" : "medium"}
          >
            Reset
          </Button>
        )}
        <Button
          onClick={onClose}
          variant="contained"
          color="error"
          size={isMobile ? "small" : "medium"}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}